import React from 'react';
import { Box, Typography, keyframes, useTheme } from '@mui/material';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const TimeUnit = ({ value, label }: { value: number; label: string }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 60,
    mx: 1,
  }}>
    <Typography 
      variant="h5" 
      component="div" 
      sx={{
        fontWeight: 'bold',
        color: 'primary.main',
        lineHeight: 1,
        mb: 0.5,
        minWidth: '2ch',
        textAlign: 'center',
      }}
    >
      {value.toString().padStart(2, '0')}
    </Typography>
    <Typography 
      variant="caption" 
      color="text.secondary"
      sx={{
        fontSize: '0.7rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const EventCountdown = ({ dateTime }: { dateTime: string }) => {
  const theme = useTheme();
  const [timeRemaining, setTimeRemaining] = React.useState(0);
  const [days, setDays] = React.useState(0);
  const [hours, setHours] = React.useState(0);
  const [minutes, setMinutes] = React.useState(0);
  const [seconds, setSeconds] = React.useState(0);
  const [isCritical, setIsCritical] = React.useState(false);

  const getTimeRemaining = React.useCallback(() => {
    const time = Date.parse(dateTime) - Date.now();
    setTimeRemaining(time);
    
    if (time <= 0) {
      setDays(0);
      setHours(0);
      setMinutes(0);
      setSeconds(0);
      return;
    }

    const totalSeconds = Math.floor(time / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    setDays(days);
    setHours(hours);
    setMinutes(minutes);
    setSeconds(seconds);
    setIsCritical(days === 0 && hours < 24);
  }, [dateTime]);

  React.useEffect(() => {
    getTimeRemaining();
    const timer = setInterval(getTimeRemaining, 1000);
    return () => clearInterval(timer);
  }, [getTimeRemaining]);

  if (timeRemaining <= 0) {
    return (
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          animation: `${pulse} 2s infinite`,
          color: theme.palette.success.main,
          fontWeight: 'bold',
        }}
      >
        <Box 
          component="span" 
          sx={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: theme.palette.success.main,
            mr: 1,
          }} 
        />
        Happening Now!
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 1,
        px: 2,
        borderRadius: 2,
        bgcolor: isCritical ? `${theme.palette.error.light}15` : 'transparent',
        transition: 'all 0.3s ease',
        border: isCritical ? `1px solid ${theme.palette.error.light}30` : 'none',
      }}
    >
      {days > 0 && <TimeUnit value={days} label="Days" />}
      <TimeUnit value={hours} label="Hours" />
      <Box sx={{ mx: 0.5, color: 'text.secondary' }}>:</Box>
      <TimeUnit value={minutes} label="Min" />
      <Box sx={{ mx: 0.5, color: 'text.secondary' }}>:</Box>
      <TimeUnit 
        value={seconds} 
        label="Sec" 
      />
    </Box>
  );
};

export default React.memo(EventCountdown);
